import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useDropzone } from 'react-dropzone';
import projectsService from '../services/projectsService';
import type { Project, ProjectCompletionFeedbackPayload } from '../types';

type RefAuth = '' | 'yes_unrestricted' | 'yes_prior_approval' | 'no';

interface FormState {
  certificate_date: string;
  project_name_reference: string;
  client_company: string;
  dear_name: string;
  project_duration_start: string;
  project_duration_end: string;
  warranty_end_text: string;
  nd_signatory_name: string;
  nd_signatory_title: string;
  client_agreement_company: string;
  client_signatory_name: string;
  client_signatory_title: string;
  overall_satisfaction: string;
  technical_quality: string;
  communication_responsiveness: string;
  schedule_timeliness: string;
  understanding_requirements: string;
  optional_comments: string;
  reference_authorization: RefAuth;
  respondent_name: string;
  respondent_company: string;
  respondent_position: string;
  respondent_signature_date: string;
}

const OCR_FILE_ACCEPT = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
} as const;

const OCR_FILE_HINT = 'PDF, DOCX, PNG, JPG, or WEBP';

function formatFileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const emptyForm = (): FormState => ({
  certificate_date: '',
  project_name_reference: '',
  client_company: '',
  dear_name: '',
  project_duration_start: '',
  project_duration_end: '',
  warranty_end_text: '',
  nd_signatory_name: '',
  nd_signatory_title: '',
  client_agreement_company: '',
  client_signatory_name: '',
  client_signatory_title: '',
  overall_satisfaction: '',
  technical_quality: '',
  communication_responsiveness: '',
  schedule_timeliness: '',
  understanding_requirements: '',
  optional_comments: '',
  reference_authorization: '',
  respondent_name: '',
  respondent_company: '',
  respondent_position: '',
  respondent_signature_date: '',
});

function buildInitialFromProject(project: Project | null): FormState {
  const base = emptyForm();
  const today = new Date().toISOString().slice(0, 10);
  const ref =
    project?.nd_number && project?.name
      ? `${project.nd_number} — ${project.name}`
      : project?.name || project?.nd_number || '';
  base.certificate_date = today;
  base.project_name_reference = ref;
  base.client_company = project?.client_name || '';
  if (project?.project_start_date) {
    base.project_duration_start = project.project_start_date.slice(0, 10);
  }
  const end = project?.project_revised_end_date || project?.project_planned_end_date;
  if (end) {
    base.project_duration_end = end.slice(0, 10);
  }
  return base;
}

function pickStr(v: unknown, fallback: string): string {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  return s || fallback;
}

function pickRating(v: unknown, fallback: string): string {
  if (v === null || v === undefined) return fallback;
  const n = Number(v);
  if (Number.isFinite(n) && n >= 1 && n <= 4) return String(Math.round(n));
  return fallback;
}

function pickRefAuth(v: unknown, fallback: RefAuth): RefAuth {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  if (s === 'yes_unrestricted' || s === 'yes_prior_approval' || s === 'no') return s;
  return fallback;
}

function applyExtractedFields(prev: FormState, fields: Record<string, unknown>): FormState {
  return {
    ...prev,
    certificate_date: pickStr(fields.certificate_date, prev.certificate_date),
    project_name_reference: pickStr(fields.project_name_reference, prev.project_name_reference),
    client_company: pickStr(fields.client_company, prev.client_company),
    dear_name: pickStr(fields.dear_name, prev.dear_name),
    project_duration_start: pickStr(fields.project_duration_start, prev.project_duration_start),
    project_duration_end: pickStr(fields.project_duration_end, prev.project_duration_end),
    warranty_end_text: pickStr(fields.warranty_end_text, prev.warranty_end_text),
    nd_signatory_name: pickStr(fields.nd_signatory_name, prev.nd_signatory_name),
    nd_signatory_title: pickStr(fields.nd_signatory_title, prev.nd_signatory_title),
    client_agreement_company: pickStr(fields.client_agreement_company, prev.client_agreement_company),
    client_signatory_name: pickStr(fields.client_signatory_name, prev.client_signatory_name),
    client_signatory_title: pickStr(fields.client_signatory_title, prev.client_signatory_title),
    overall_satisfaction: pickRating(fields.overall_satisfaction, prev.overall_satisfaction),
    technical_quality: pickRating(fields.technical_quality, prev.technical_quality),
    communication_responsiveness: pickRating(fields.communication_responsiveness, prev.communication_responsiveness),
    schedule_timeliness: pickRating(fields.schedule_timeliness, prev.schedule_timeliness),
    understanding_requirements: pickRating(fields.understanding_requirements, prev.understanding_requirements),
    optional_comments: pickStr(fields.optional_comments, prev.optional_comments),
    reference_authorization: pickRefAuth(fields.reference_authorization, prev.reference_authorization),
    respondent_name: pickStr(fields.respondent_name, prev.respondent_name),
    respondent_company: pickStr(fields.respondent_company, prev.respondent_company),
    respondent_position: pickStr(fields.respondent_position, prev.respondent_position),
    respondent_signature_date: pickStr(fields.respondent_signature_date, prev.respondent_signature_date),
  };
}

function toPayload(f: FormState): ProjectCompletionFeedbackPayload {
  const num = (s: string) => (s === '' ? null : Number(s));
  return {
    certificate_date: f.certificate_date || null,
    project_name_reference: f.project_name_reference || null,
    client_company: f.client_company || null,
    dear_name: f.dear_name || null,
    project_duration_start: f.project_duration_start || null,
    project_duration_end: f.project_duration_end || null,
    warranty_end_text: f.warranty_end_text || null,
    nd_signatory_name: f.nd_signatory_name || null,
    nd_signatory_title: f.nd_signatory_title || null,
    client_agreement_company: f.client_agreement_company || null,
    client_signatory_name: f.client_signatory_name || null,
    client_signatory_title: f.client_signatory_title || null,
    overall_satisfaction: num(f.overall_satisfaction),
    technical_quality: num(f.technical_quality),
    communication_responsiveness: num(f.communication_responsiveness),
    schedule_timeliness: num(f.schedule_timeliness),
    understanding_requirements: num(f.understanding_requirements),
    optional_comments: f.optional_comments || null,
    reference_authorization: f.reference_authorization === '' ? null : f.reference_authorization,
    respondent_name: f.respondent_name || null,
    respondent_company: f.respondent_company || null,
    respondent_position: f.respondent_position || null,
    respondent_signature_date: f.respondent_signature_date || null,
  };
}

function RatingRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Grid item xs={12}>
      <FormControl component="fieldset" variant="standard" fullWidth>
        <FormLabel component="legend" sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary' }}>
          {label}
        </FormLabel>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
          4 = above expectations · 3 / 2 = good · 1 = below expectations
        </Typography>
        <RadioGroup row value={value} onChange={(e) => onChange(e.target.value)}>
          <FormControlLabel value="" control={<Radio size="small" />} label="—" />
          {[1, 2, 3, 4].map((n) => (
            <FormControlLabel key={n} value={String(n)} control={<Radio size="small" />} label={String(n)} />
          ))}
        </RadioGroup>
      </FormControl>
    </Grid>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: number;
  project: Project | null;
  alreadyEntered?: boolean;
}

const ProjectCompletionFeedbackDialog: React.FC<Props> = ({
  open,
  onClose,
  projectId,
  project,
  alreadyEntered = false,
}) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrFile, setOcrFile] = useState<File | null>(null);
  const [prefillSourceFile, setPrefillSourceFile] = useState<File | null>(null);
  const [ocrPreview, setOcrPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [history, setHistory] = useState<
    Array<{ id: number; submitted_by_name: string; created_at: string | null }>
  >([]);
  const [lockedAfterSubmit, setLockedAfterSubmit] = useState(false);

  const resetForOpen = useCallback(() => {
    setForm(buildInitialFromProject(project));
    setError(null);
    setSuccess(null);
    setOcrFile(null);
    setPrefillSourceFile(null);
    setOcrPreview(null);
  }, [project]);

  useEffect(() => {
    if (!open) return;
    resetForOpen();
    setLockedAfterSubmit(false);
  }, [open, resetForOpen]);

  useEffect(() => {
    if (!open || !projectId) return;
    projectsService
      .listCompletionFeedback(projectId, 10)
      .then((r) =>
        setHistory(
          r.items.map((i) => ({
            id: i.id,
            submitted_by_name: i.submitted_by_name,
            created_at: i.created_at,
          }))
        )
      )
      .catch(() => setHistory([]));
  }, [open, projectId, success]);

  const applyOcrFile = useCallback((file: File | null) => {
    setOcrFile(file);
    setPrefillSourceFile(null);
    setOcrPreview(null);
    if (file) setError(null);
  }, []);

  const onOcrDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) applyOcrFile(file);
    },
    [applyOcrFile]
  );

  const onOcrDropRejected = useCallback(() => {
    setError(`That file type is not supported. Use ${OCR_FILE_HINT}.`);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onOcrDrop,
    onDropRejected: onOcrDropRejected,
    accept: OCR_FILE_ACCEPT,
    multiple: false,
    disabled: ocrBusy,
    noDragEventsBubbling: true,
  });

  const handleOcrPrefill = async () => {
    if (!ocrFile) {
      setError('Choose a PDF, DOCX, or image file first.');
      return;
    }
    setOcrBusy(true);
    setError(null);
    setOcrPreview(null);
    try {
      const { fields, ocr_markdown_preview } = await projectsService.ocrPrefillCompletionFeedback(projectId, ocrFile);
      setForm((prev) => applyExtractedFields(prev, fields));
      setPrefillSourceFile(ocrFile);
      setOcrPreview(ocr_markdown_preview || null);
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e ? (e as { response?: { data?: { error?: string } } }).response?.data?.error : null;
      setError(msg || 'OCR or extraction failed. Try another file or fill the form manually.');
      setPrefillSourceFile(null);
    } finally {
      setOcrBusy(false);
    }
  };

  const feedbackAlreadyEntered = alreadyEntered || history.length > 0 || lockedAfterSubmit;

  const handleSubmit = async () => {
    if (feedbackAlreadyEntered) {
      setError('Feedback has already been entered for this project. Open the Feedback tab to view or edit it.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await projectsService.submitCompletionFeedback(projectId, toPayload(form), prefillSourceFile);
      resetForOpen();
      setSuccess('Feedback saved for this project.');
      setLockedAfterSubmit(true);
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e ? (e as { response?: { data?: { error?: string } } }).response?.data?.error : null;
      setError(msg || 'Failed to save feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="h6" component="span">
            Enter feedback (completion certificate / questionnaire)
          </Typography>
          <IconButton onClick={onClose} aria-label="close" size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => e.preventDefault()}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Matches the Project Completion Letter and Client Feedback &amp; Satisfaction form. You can fill fields manually
          or upload a signed PDF, DOCX export, or scan (PNG/JPG) to prefill via local AI (Mistral fallback if unavailable).
        </Typography>

        <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 2, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" gap={1}>
              <AutoAwesomeIcon color="primary" fontSize="small" />
              <Typography fontWeight={600}>Prefill from document (local AI, Mistral fallback)</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed',
                borderColor: isDragActive ? 'primary.main' : ocrFile ? 'primary.light' : 'divider',
                borderRadius: 1,
                px: 2,
                py: 2.5,
                mb: 1.5,
                textAlign: 'center',
                cursor: ocrBusy ? 'wait' : 'pointer',
                outline: 'none',
                bgcolor: isDragActive ? 'action.selected' : 'transparent',
                transition: 'border-color 0.15s, background-color 0.15s',
                '&:hover': ocrBusy
                  ? {}
                  : {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover',
                    },
              }}
            >
              <input {...getInputProps()} />
              {ocrFile && !isDragActive ? (
                <Box>
                  <InsertDriveFileOutlinedIcon sx={{ fontSize: 32, color: 'primary.main', mb: 0.5 }} />
                  <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                    {ocrFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formatFileSize(ocrFile.size)} · click or drop to replace
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 32,
                      color: isDragActive ? 'primary.main' : 'text.secondary',
                      mb: 0.5,
                    }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={isDragActive ? 'primary.main' : 'text.primary'}
                  >
                    {isDragActive ? 'Drop to attach' : 'Drag & drop a document here, or click to browse'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Signed PDF, DOCX export, or scan ({OCR_FILE_HINT})
                  </Typography>
                </Box>
              )}
            </Box>
            <Box display="flex" justifyContent="flex-end" sx={{ mb: 1 }}>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<AutoAwesomeIcon />}
                onClick={handleOcrPrefill}
                disabled={ocrBusy || !ocrFile}
              >
                Extract &amp; prefill
              </Button>
            </Box>
            {ocrBusy && <LinearProgress sx={{ mb: 1 }} />}
            {ocrPreview && (
              <TextField
                label="OCR preview (truncated)"
                value={ocrPreview}
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                InputProps={{ readOnly: true }}
                size="small"
                sx={{ mt: 1 }}
              />
            )}
          </AccordionDetails>
        </Accordion>

        {history.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Recent submissions
            </Typography>
            {history.map((h) => (
              <Typography key={h.id} variant="caption" display="block">
                #{h.id} · {h.submitted_by_name}
                {h.created_at ? ` · ${h.created_at}` : ''}
              </Typography>
            ))}
          </Alert>
        )}

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Letter header
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Certificate date"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.certificate_date}
              onChange={(e) => setForm({ ...form, certificate_date: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Project (name / reference)"
              fullWidth
              size="small"
              value={form.project_name_reference}
              onChange={(e) => setForm({ ...form, project_name_reference: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Client company"
              fullWidth
              size="small"
              value={form.client_company}
              onChange={(e) => setForm({ ...form, client_company: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Dear (name)"
              fullWidth
              size="small"
              value={form.dear_name}
              onChange={(e) => setForm({ ...form, dear_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Project duration — start"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.project_duration_start}
              onChange={(e) => setForm({ ...form, project_duration_start: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Project duration — completion"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.project_duration_end}
              onChange={(e) => setForm({ ...form, project_duration_end: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Warranty / completion notes"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={form.warranty_end_text}
              onChange={(e) => setForm({ ...form, warranty_end_text: e.target.value })}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Signatories (letter)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nautilus Dynamics — name"
              fullWidth
              size="small"
              value={form.nd_signatory_name}
              onChange={(e) => setForm({ ...form, nd_signatory_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nautilus Dynamics — title"
              fullWidth
              size="small"
              value={form.nd_signatory_title}
              onChange={(e) => setForm({ ...form, nd_signatory_title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Client agreement — company"
              fullWidth
              size="small"
              value={form.client_agreement_company}
              onChange={(e) => setForm({ ...form, client_agreement_company: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Client — signatory name"
              fullWidth
              size="small"
              value={form.client_signatory_name}
              onChange={(e) => setForm({ ...form, client_signatory_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Client — signatory title"
              fullWidth
              size="small"
              value={form.client_signatory_title}
              onChange={(e) => setForm({ ...form, client_signatory_title: e.target.value })}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Client feedback &amp; satisfaction
        </Typography>
        <Grid container spacing={2}>
          <RatingRow
            label="Overall satisfaction"
            value={form.overall_satisfaction}
            onChange={(v) => setForm({ ...form, overall_satisfaction: v })}
          />
          <RatingRow
            label="Technical quality of deliverables"
            value={form.technical_quality}
            onChange={(v) => setForm({ ...form, technical_quality: v })}
          />
          <RatingRow
            label="Communication & responsiveness"
            value={form.communication_responsiveness}
            onChange={(v) => setForm({ ...form, communication_responsiveness: v })}
          />
          <RatingRow
            label="Schedule & timeliness"
            value={form.schedule_timeliness}
            onChange={(v) => setForm({ ...form, schedule_timeliness: v })}
          />
          <RatingRow
            label="Understanding of requirements"
            value={form.understanding_requirements}
            onChange={(v) => setForm({ ...form, understanding_requirements: v })}
          />
          <Grid item xs={12}>
            <TextField
              label="Optional comments (voice of customer)"
              fullWidth
              size="small"
              multiline
              minRows={3}
              value={form.optional_comments}
              onChange={(e) => setForm({ ...form, optional_comments: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Reference authorization"
              fullWidth
              size="small"
              value={form.reference_authorization}
              onChange={(e) => setForm({ ...form, reference_authorization: e.target.value as RefAuth })}
              helperText="May we include your organization as a reference?"
            >
              <MenuItem value="">
                <em>Not specified</em>
              </MenuItem>
              <MenuItem value="yes_unrestricted">Yes, without restriction</MenuItem>
              <MenuItem value="yes_prior_approval">Yes, subject to prior approval</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Respondent (footer)
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name"
              fullWidth
              size="small"
              value={form.respondent_name}
              onChange={(e) => setForm({ ...form, respondent_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Company"
              fullWidth
              size="small"
              value={form.respondent_company}
              onChange={(e) => setForm({ ...form, respondent_company: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Position"
              fullWidth
              size="small"
              value={form.respondent_position}
              onChange={(e) => setForm({ ...form, respondent_position: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Date"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.respondent_signature_date}
              onChange={(e) => setForm({ ...form, respondent_signature_date: e.target.value })}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || feedbackAlreadyEntered}
        >
          {submitting ? 'Saving…' : 'Submit feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectCompletionFeedbackDialog;
